import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatPercent, monthNames } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, TrendingUp, Save, Calculator, FileText, History } from "lucide-react";

export const CATrackingPage = () => {
  const [orders, setOrders] = useState([]);
  const [monthlyCA, setMonthlyCA] = useState(null);
  const [importedCA, setImportedCA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [localProgress, setLocalProgress] = useState({});

  const currentYear = new Date().getFullYear();
  // Historique jusqu'à 2015
  const years = Array.from({ length: currentYear - 2015 + 2 }, (_, i) => currentYear + 1 - i);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get all orders with cumulative progress from the new endpoint
      const ordersRes = await api.getAllOrdersWithProgress({ 
        year: selectedYear, 
        month: selectedMonth 
      });
      setOrders(ordersRes.data);
      
      // Initialize local progress with current month progress
      const progressMap = {};
      ordersRes.data.forEach(o => {
        progressMap[o.order_id] = o.current_month_progress;
      });
      setLocalProgress(progressMap);
      
      // Get monthly CA calculation
      const caRes = await api.getMonthlyCAFromProgress({ 
        year: selectedYear, 
        month: selectedMonth 
      });
      setMonthlyCA(caRes.data);
      
      // Get imported CA for this month/year
      try {
        const importedRes = await api.getImportedData();
        const importedForMonth = importedRes.data.find(
          d => d.year === selectedYear && d.month === selectedMonth
        );
        setImportedCA(importedForMonth || null);
      } catch {
        setImportedCA(null);
      }
      
    } catch (error) {
      toast.error("Erreur lors du chargement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProgressChange = (orderId, value, maxAllowed) => {
    // Limit value to max allowed (100 - cumulative before)
    const newValue = Math.min(value[0], maxAllowed);
    setLocalProgress(prev => ({
      ...prev,
      [orderId]: newValue
    }));
  };

  const handleSaveProgress = async (orderId) => {
    try {
      setSaving(true);
      await api.createOrderProgress({
        order_id: orderId,
        year: selectedYear,
        month: selectedMonth,
        progress_percent: localProgress[orderId] || 0
      });
      toast.success("Avancement enregistré");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllProgress = async () => {
    try {
      setSaving(true);
      const promises = orders.map(order => {
        const progressValue = localProgress[order.order_id] ?? order.current_month_progress ?? 0;
        return api.createOrderProgress({
          order_id: order.order_id,
          year: selectedYear,
          month: selectedMonth,
          progress_percent: progressValue
        });
      });
      await Promise.all(promises);
      toast.success("Tous les avancements ont été enregistrés");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const calculateCAForOrder = (order) => {
    const progressValue = localProgress[order.order_id] ?? order.current_month_progress ?? 0;
    return order.amount_ht * progressValue / 100;
  };

  const totalEstimatedCA = orders.reduce((sum, order) => sum + calculateCAForOrder(order), 0);

  return (
    <div className="space-y-6 animate-in" data-testid="ca-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Suivi du CA</h1>
          <p className="text-muted-foreground mt-1">
            Définissez l'avancement de chaque commande pour calculer le CA mensuel
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="ca-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="ca-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* CA Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* CA Importé (historique) */}
        {importedCA && (
          <Card className="kpi-card border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center">
                  <History className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">CA importé (historique)</p>
                  <p className="text-2xl font-heading font-bold currency text-amber-600">
                    {formatCurrency(importedCA.ca || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <Calculator className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CA estimé du mois</p>
                <p className="text-2xl font-heading font-bold currency text-primary">
                  {formatCurrency(totalEstimatedCA)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-green-100 rounded-sm flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CA enregistré</p>
                <p className="text-2xl font-heading font-bold currency text-green-600">
                  {formatCurrency(monthlyCA?.total_ca || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-secondary/10 rounded-sm flex items-center justify-center">
                <FileText className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Commandes suivies</p>
                <p className="text-2xl font-heading font-bold">{orders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Calculator className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Comment ça marche ? (Suivi cumulatif)</h3>
              <p className="text-sm text-blue-800 mt-1">
                Cette page affiche <strong>toutes les commandes non terminées</strong>. Pour chaque commande, 
                l'avancement est <strong>cumulatif</strong> : si une commande a été facturée à 20% en janvier, 
                elle démarrera à 20% en février.
                <br /><br />
                <strong>• Avancement cumulé</strong> = Somme des avancements des mois précédents<br />
                <strong>• Progression ce mois</strong> = Ce que vous ajoutez ce mois-ci (limité à 100% - cumulé)<br />
                <strong>• CA du mois</strong> = Montant HT × % d'avancement ce mois
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Progress */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">
            Avancement des commandes - {monthNames[selectedMonth - 1]} {selectedYear}
          </CardTitle>
          <Button 
            onClick={handleSaveAllProgress} 
            disabled={saving}
            data-testid="save-all-progress-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Tout enregistrer
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune commande en cours à suivre</p>
              <p className="text-sm text-muted-foreground mt-1">
                Toutes les commandes sont terminées (100% d'avancement) ou il n'y a pas de commandes actives.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => {
                const currentProgress = localProgress[order.order_id] ?? order.current_month_progress ?? 0;
                const cumulativeBefore = order.cumulative_before ?? 0;
                const remainingProgress = order.remaining_progress ?? (100 - cumulativeBefore);
                const totalProgress = cumulativeBefore + currentProgress;
                const caGenerated = order.amount_ht * currentProgress / 100;
                const hasChanged = localProgress[order.order_id] !== undefined && 
                                   localProgress[order.order_id] !== (order.current_month_progress ?? 0);
                
                return (
                  <div 
                    key={order.order_id} 
                    className="p-4 border rounded-sm space-y-4"
                    data-testid={`order-progress-${order.order_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-muted-foreground">
                            {order.reference}
                          </span>
                          <Badge variant="outline">{order.order_type_name}</Badge>
                          {cumulativeBefore > 0 && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <History className="h-3 w-3" />
                              {cumulativeBefore}% cumulé
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-medium mt-1">{order.client_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          Montant HT: <span className="currency font-medium">{formatCurrency(order.amount_ht)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">CA généré ce mois</p>
                        <p className="text-xl font-heading font-bold currency text-primary">
                          {formatCurrency(caGenerated)}
                        </p>
                      </div>
                    </div>

                    {/* Cumulative progress bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progression totale</span>
                        <span className="font-medium">{totalProgress.toFixed(0)}% / 100%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                        {cumulativeBefore > 0 && (
                          <div 
                            className="bg-gray-400 h-full" 
                            style={{ width: `${cumulativeBefore}%` }}
                            title={`Cumulé des mois précédents: ${cumulativeBefore}%`}
                          />
                        )}
                        <div 
                          className="bg-primary h-full transition-all" 
                          style={{ width: `${currentProgress}%` }}
                          title={`Ce mois: ${currentProgress}%`}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Déjà facturé: {cumulativeBefore}%</span>
                        <span>Ce mois: {currentProgress}%</span>
                        <span>Restant: {Math.max(0, 100 - totalProgress).toFixed(0)}%</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Avancement ce mois (max: {remainingProgress}%)</Label>
                        <span className="font-heading font-bold text-lg">
                          {currentProgress}%
                        </span>
                      </div>
                      <Slider
                        value={[currentProgress]}
                        onValueChange={(v) => handleProgressChange(order.order_id, v, remainingProgress)}
                        max={remainingProgress}
                        step={5}
                        className="w-full"
                        disabled={remainingProgress === 0}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0%</span>
                        <span>{Math.round(remainingProgress / 4)}%</span>
                        <span>{Math.round(remainingProgress / 2)}%</span>
                        <span>{Math.round(remainingProgress * 3 / 4)}%</span>
                        <span>{remainingProgress}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2">
                      <div>
                        {hasChanged && (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                            Non enregistré
                          </Badge>
                        )}
                        {!hasChanged && order.current_month_progress > 0 && (
                          <Badge className="bg-green-100 text-green-800">
                            Enregistré
                          </Badge>
                        )}
                        {remainingProgress === 0 && (
                          <Badge className="bg-blue-100 text-blue-800">
                            Commande à 100%
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={hasChanged ? "default" : "outline"}
                        onClick={() => handleSaveProgress(order.order_id)}
                        disabled={saving || remainingProgress === 0}
                        data-testid={`save-progress-${order.order_id}`}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      {monthlyCA && monthlyCA.details && monthlyCA.details.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Récapitulatif enregistré</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Client</th>
                    <th>Montant HT</th>
                    <th>Avancement</th>
                    <th>CA généré</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyCA.details.map((detail, idx) => (
                    <tr key={idx}>
                      <td className="font-mono text-sm">{detail.order_reference}</td>
                      <td>{detail.order_client}</td>
                      <td className="currency">{formatCurrency(detail.order_amount)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Progress value={detail.progress_percent} className="w-20 h-2" />
                          <span className="text-sm font-medium">{detail.progress_percent}%</span>
                        </div>
                      </td>
                      <td className="currency font-medium text-primary">
                        {formatCurrency(detail.ca_generated)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted font-medium">
                    <td colSpan="4">Total CA du mois</td>
                    <td className="currency text-primary font-heading text-lg">
                      {formatCurrency(monthlyCA.total_ca)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CATrackingPage;
